import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Database, RefreshCw, Search, Filter } from "lucide-react";
import { zktecoAPI } from "../services/api";
import BiometricStatsCards from "../components/biometric/BiometricStatsCards";
import BiometricSyncHistory from "../components/biometric/BiometricSyncHistory";
import BiometricUnmappedTable from "../components/biometric/BiometricUnmappedTable";
import BiometricMappingModal from "../components/biometric/BiometricMappingModal";
import BiometricDetailsModal from "../components/biometric/BiometricDetailsModal";

const BiometricMapping = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [mapping, setMapping] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedRegistrationNumber, setSelectedRegistrationNumber] = useState("");
  const [employeeSearchResults, setEmployeeSearchResults] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [biometricDetails, setBiometricDetails] = useState({});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItemDetails, setSelectedItemDetails] = useState(null);

  const [unmappedQ, syncHistoryQ] = useQueries({
    queries: [
      {
        queryKey: ["biometric-unmapped"],
        queryFn: () =>
          zktecoAPI.getUnmappedData(200).then((r) => {
            if (!r.data.success) throw new Error("load_failed");
            return r.data.unmapped_data || [];
          }),
      },
      {
        queryKey: ["biometric-sync-history"],
        queryFn: () => zktecoAPI.getSyncHistory(null, 20).then((r) => r.data || []),
      },
    ],
  });

  useEffect(() => {
    if (unmappedQ.isError) toast.error(t("biometricMapping.toasts.loadUnmappedImpossible"));
  }, [unmappedQ.isError]);

  const unmappedData = unmappedQ.data ?? [];
  const syncHistory = syncHistoryQ.data ?? [];
  const loading = unmappedQ.isPending;

  const searchEmployees = async (query) => {
    if (!query || query.length < 2) { setEmployeeSearchResults([]); return; }
    try {
      const response = await zktecoAPI.searchEmployees(query, 50);
      setEmployeeSearchResults(response.data.employees || []);
    } catch (error) {
      toast.error(t("biometricMapping.toasts.searchEmployeesError"));
    }
  };

  const handleManualMapping = async () => {
    if (!selectedEmployee || !selectedRegistrationNumber) {
      toast.error(t("biometricMapping.toasts.selectEmployeeAndNumber"));
      return;
    }
    try {
      setMapping(true);
      const response = await zktecoAPI.mapUserData({
        registration_number: selectedRegistrationNumber,
        employee_id: selectedEmployee.id,
      });
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        if (response.data.biometric_records_updated || response.data.photos_updated) {
          toast.info(
            `📊 ${response.data.biometric_records_updated || 0} ${t("biometricMapping.toasts.mappingSuccess")} & ${response.data.photos_updated || 0}`,
            { duration: 5000 }
          );
        }
        setShowMappingModal(false);
        queryClient.invalidateQueries({ queryKey: ["biometric-unmapped"] });
        resetMappingForm();
      } else {
        toast.error(`❌ ${response.data.message}`);
      }
    } catch (error) {
      toast.error(t("biometricMapping.toasts.mappingError"));
    } finally {
      setMapping(false);
    }
  };

  const resetMappingForm = () => {
    setSelectedEmployee(null);
    setSelectedRegistrationNumber("");
    setEmployeeSearchTerm("");
    setEmployeeSearchResults([]);
  };

  const openMappingModal = (registrationNumber) => {
    setSelectedRegistrationNumber(registrationNumber);
    setShowMappingModal(true);
  };

  const viewItemDetails = async (item) => {
    try {
      setSelectedItemDetails(item);
      const details = {};
      for (const terminal of item.terminals) {
        try {
          const [biometricResponse, photosResponse] = await Promise.all([
            zktecoAPI.getTerminalBiometricData(terminal, { mappedOnly: false, limit: 100 }),
            zktecoAPI.getTerminalPhotos(terminal, { mappedOnly: false, includePhotoData: false, limit: 10 }),
          ]);
          details[terminal] = {
            biometric: biometricResponse.data.filter((b) => b.registration_number === item.registration_number),
            photos: photosResponse.data.filter((p) => p.registration_number === item.registration_number),
          };
        } catch (error) {
          details[terminal] = { biometric: [], photos: [] };
        }
      }
      setBiometricDetails(details);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error(t("biometricMapping.toasts.loadDetailsError"));
    }
  };

  const filteredUnmappedData = unmappedData.filter((item) =>
    item.registration_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("biometricMapping.title")}</h1>
          <p className="text-muted-foreground">{t("biometricMapping.subtitle")}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={() => setShowHistory(!showHistory)}>
            <Database className="h-4 w-4 mr-2" />
            {showHistory ? t("biometricMapping.hideHistory") : t("biometricMapping.showHistory")}
          </Button>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ["biometric-unmapped"] })} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {t("biometricMapping.refresh")}
          </Button>
        </div>
      </div>

      <BiometricStatsCards filteredUnmappedData={filteredUnmappedData} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {t("biometricMapping.filtersTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder={t("biometricMapping.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {showHistory && <BiometricSyncHistory syncHistory={syncHistory} />}

      <BiometricUnmappedTable
        loading={loading}
        filteredUnmappedData={filteredUnmappedData}
        viewItemDetails={viewItemDetails}
        openMappingModal={openMappingModal}
      />

      <BiometricMappingModal
        open={showMappingModal}
        onOpenChange={setShowMappingModal}
        selectedRegistrationNumber={selectedRegistrationNumber}
        employeeSearchTerm={employeeSearchTerm}
        setEmployeeSearchTerm={setEmployeeSearchTerm}
        employeeSearchResults={employeeSearchResults}
        selectedEmployee={selectedEmployee}
        setSelectedEmployee={setSelectedEmployee}
        searchEmployees={searchEmployees}
        mapping={mapping}
        handleManualMapping={handleManualMapping}
        resetMappingForm={resetMappingForm}
      />

      <BiometricDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        selectedItemDetails={selectedItemDetails}
        biometricDetails={biometricDetails}
      />
    </div>
  );
};

export default BiometricMapping;
